/**
 * MessageBubble Component - HYB Shop Chat
 * Single message bubble with position-aware styling
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../utils/responsive';
import { ReadReceipt, MessageStatus } from '../ReadReceipt';

type BubblePosition = 'first' | 'middle' | 'last' | 'single';

interface Message {
  id: string;
  text?: string;
  images?: string[];
  type?: 'text' | 'image';
  createdAt?: string;
  status?: MessageStatus;
}

interface MessageBubbleProps {
  message: Message;
  isAdmin: boolean;
  position?: BubblePosition;
  showTimestamp?: boolean;
  className?: string;
}

/**
 * Get rounded corner classes based on position and sender
 */
function getRoundedClasses(position: BubblePosition, isAdmin: boolean): string {
  // Base: all corners rounded
  // Adjust specific corners based on position to create stacking effect

  if (isAdmin) {
    // Admin messages: right-aligned, adjust right corners
    switch (position) {
      case 'first':
        return 'rounded-2xl rounded-br-md';
      case 'middle':
        return 'rounded-2xl rounded-r-md';
      case 'last':
        return 'rounded-2xl rounded-tr-md';
      case 'single':
      default:
        return 'rounded-2xl rounded-br-md';
    }
  } else {
    // User messages: left-aligned, adjust left corners
    switch (position) {
      case 'first':
        return 'rounded-2xl rounded-bl-md';
      case 'middle':
        return 'rounded-2xl rounded-l-md';
      case 'last':
        return 'rounded-2xl rounded-tl-md';
      case 'single':
      default:
        return 'rounded-2xl rounded-bl-md';
    }
  }
}

export function MessageBubble({
  message,
  isAdmin,
  position = 'single',
  showTimestamp = true,
  className,
}: MessageBubbleProps) {
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const roundedClasses = getRoundedClasses(position, isAdmin);

  const formattedTime = message.createdAt
    ? new Date(message.createdAt).toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
    })
    : null;

  return (
    <div
      className={cn(
        'flex',
        isAdmin ? 'justify-end' : 'justify-start',
        className
      )}
    >
      <div
        className={cn(
          'max-w-[85%] lg:max-w-[70%]',
          'w-max', // Force usage of available width up to max-w, prevents premature wrapping
          'min-w-[80px]',
          'px-4 py-2.5',
          'text-sm leading-relaxed',
          'shadow-sm',
          'transition-transform duration-200 ease-out',
          'hover:scale-[1.01]',
          roundedClasses,
          isAdmin && 'bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500',
          isAdmin && 'text-white',
          isAdmin && 'shadow-amber-200/30 dark:shadow-none',
          !isAdmin && 'bg-white dark:bg-gray-800',
          !isAdmin && 'text-stone-900 dark:text-gray-100',
          !isAdmin && 'border border-stone-200 dark:border-gray-700'
        )}
        role="article"
        aria-label={`ข้อความจาก ${isAdmin ? 'แอดมิน' : 'ลูกค้า'}`}
      >
        {/* Image attachments */}
        {message.type === 'image' && message.images && message.images.length > 0 && (
          <div className="mb-2 space-y-2">
            {message.images.map((imageUrl, idx) => (
              <img
                key={idx}
                src={imageUrl}
                alt={`แนบไฟล์ ${idx + 1}`}
                className={cn(
                  'max-h-48 w-full rounded-lg object-cover',
                  'cursor-pointer',
                  'transition-all duration-200',
                  'hover:opacity-90 hover:scale-[1.02]'
                )}
                onClick={() => setFullscreenImage(imageUrl)}
                loading="lazy"
                onError={(e) => {
                  // Hide broken images
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ))}
          </div>
        )}

        {/* Text content */}
        {message.text && (
          <p className="whitespace-pre-wrap break-words text-left">{message.text}</p>
        )}

        {/* Timestamp and Read Receipt */}
        {showTimestamp && formattedTime && (
          <div className={cn(
            'mt-1.5 flex items-center justify-end gap-1.5',
            'text-[10px]',
            isAdmin ? 'text-white/70' : 'text-stone-400 dark:text-gray-500'
          )}>
            <span>{formattedTime}</span>
            {isAdmin && message.status && (
              <ReadReceipt
                status={message.status}
                className={isAdmin ? 'text-white/80' : ''}
              />
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setFullscreenImage(null)}
        >
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenImage(null);
            }}
            className={cn(
              'absolute top-4 right-4 z-50',
              'p-2 rounded-full',
              'bg-white/20 hover:bg-white/30',
              'text-white',
              'transition-colors duration-200'
            )}
            aria-label="ปิด"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Image */}
          <img
            src={fullscreenImage}
            alt="รูปภาพขนาดเต็ม"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
